import React from 'react';

/**
 * StatusBadge: Small color-coded chip for status indicators.
 */
export function StatusBadge({
  label,
  variant = 'neutral',
  icon
}: {
  label: string;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'diamond' | 'gold' | 'silver' | 'prospect';
  icon?: React.ReactNode;
}) {
  const variants = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    warning: 'bg-amber-50 text-amber-700 border-amber-100',
    danger: 'bg-rose-50 text-rose-700 border-rose-100',
    info: 'bg-sky-50 text-sky-700 border-sky-100',
    neutral: 'bg-slate-50 text-slate-700 border-slate-100',
    diamond: 'bg-blue-50 text-blue-700 border-blue-100',
    gold: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    silver: 'bg-gray-50 text-gray-700 border-gray-100',
    prospect: 'bg-violet-50 text-violet-700 border-violet-100',
  };

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium leading-none uppercase tracking-wider ${variants[variant]}`}>
      {icon && <span className="mr-1">{icon}</span>}
      {label}
    </span>
  );
}

/**
 * CompactStatCard: A high-density stat display.
 */
export function CompactStatCard({
  label,
  value,
  subValue,
  variant = 'neutral',
  onClick
}: {
  label: string;
  value: string | number;
  subValue?: string;
  variant?: 'neutral' | 'warning' | 'danger' | 'success';
  onClick?: () => void;
}) {
  const borderColors = {
    neutral: 'border-hairline',
    warning: 'border-amber-200 bg-amber-50/30',
    danger: 'border-rose-200 bg-rose-50/30',
    success: 'border-emerald-200 bg-emerald-50/30',
  };

  return (
    <div
      className={`p-2 border rounded shadow-sm ${borderColors[variant]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <p className="text-[10px] uppercase tracking-tight text-ink-mute font-semibold mb-0.5">{label}</p>
      <p className="text-sm font-bold text-ink truncate leading-tight">{value}</p>
      {subValue && <p className="text-[10px] text-ink-mute truncate mt-0.5">{subValue}</p>}
    </div>
  );
}

/**
 * DenseAccordion: Minimalist expandable section.
 */
export function DenseAccordion({
  title,
  children,
  defaultOpen = false,
  badge
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className="border-b border-hairline last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2 px-1 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={`text-[10px] transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
          <span className="text-xs font-bold uppercase tracking-wide text-ink">{title}</span>
          {badge}
        </div>
      </button>
      {isOpen && (
        <div className="pb-4 px-1">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * DataRow: A compact key-value pair for lists.
 */
export function DataRow({ label, value, subLabel }: { label: string; value: React.ReactNode; subLabel?: string }) {
  return (
    <div className="flex justify-between items-baseline py-1 border-b border-hairline border-dotted last:border-0 group">
      <div className="flex flex-col">
        <span className="text-[11px] text-ink-mute group-hover:text-ink transition-colors">{label}</span>
        {subLabel && <span className="text-[9px] text-ink-mute italic">{subLabel}</span>}
      </div>
      <span className="text-xs font-medium text-ink">{value}</span>
    </div>
  );
}
